import { ConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import type { Family } from "@/lib/family";
import type { HistoryEntry } from "@/lib/history";
import * as Tabs from "@radix-ui/react-tabs";
import { Copy, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResultAnomaly } from "./result-anomaly";
import { ResultForecast } from "./result-forecast";
import { ResultTabular } from "./result-tabular";
import { WarningsBanner } from "./warnings-banner";

export interface ResponsePanelProps {
  result: HistoryEntry | null;
  family: Family | null;
  history: HistoryEntry[];
  onRestore: (entry: HistoryEntry) => void;
  onClear: () => void;
}

function statusColor(status: number): string {
  if (status === 0) return "text-danger";
  if (status >= 200 && status < 300) return "text-success";
  if (status >= 400) return "text-danger";
  return "text-fg-muted";
}

function formatTs(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function pickPredictions(body: unknown): unknown[] | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const p = obj.predictions;
  if (Array.isArray(p)) return p;
  return null;
}

function pickForecast(body: unknown): { horizon: number; freq: string; groups: unknown[] } | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const f = obj.forecast;
  if (!f || typeof f !== "object") return null;
  const fo = f as Record<string, unknown>;
  if (typeof fo.horizon === "number" && typeof fo.freq === "string" && Array.isArray(fo.groups)) {
    return { horizon: fo.horizon, freq: fo.freq, groups: fo.groups };
  }
  return null;
}

export function ResponsePanel({ result, family, history, onRestore, onClear }: ResponsePanelProps) {
  const [tab, setTab] = useState<"response" | "headers">("response");
  const [showRaw, setShowRaw] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);

  // Reset to structured view whenever a new result arrives.
  // biome-ignore lint/correctness/useExhaustiveDependencies: result.ts is the intentional trigger
  useEffect(() => {
    setShowRaw(false);
  }, [result?.ts]);

  const handleCopyResponse = () => {
    if (!result) return;
    const text = JSON.stringify(result.response ?? result.error, null, 2);
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  };

  const headersText = result?.headers ? JSON.stringify(result.headers, null, 2) : "—";

  const isError = !!result?.error || (result?.status != null && result.status >= 400);
  const body = result?.response;
  const warnings = (result?.warnings ?? []) as Array<Record<string, unknown>>;

  const renderStructured = () => {
    if (!result) return null;
    if (isError) {
      return (
        <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-[11px] text-fg leading-relaxed whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
          {result.error ?? JSON.stringify(body, null, 2)}
        </pre>
      );
    }
    if (family === "forecast") {
      const f = pickForecast(body);
      if (f) {
        return <ResultForecast forecast={f as Parameters<typeof ResultForecast>[0]["forecast"]} />;
      }
    }
    if (family === "anomaly") {
      const p = pickPredictions(body);
      if (p) {
        return (
          <ResultAnomaly
            predictions={p as Parameters<typeof ResultAnomaly>[0]["predictions"]}
            thresholdUsed={result.threshold_used ?? null}
          />
        );
      }
    }
    if (family === "tabular") {
      const p = pickPredictions(body);
      if (p) {
        return (
          <ResultTabular predictions={p as Parameters<typeof ResultTabular>[0]["predictions"]} />
        );
      }
    }
    // fallback for unknown shape — show raw
    return (
      <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-[11px] text-fg leading-relaxed whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
        {JSON.stringify(body, null, 2)}
      </pre>
    );
  };

  return (
    <div className="flex flex-col rounded-lg border border-border bg-surface overflow-hidden">
      {/* Stat cells row */}
      <div className="grid grid-cols-4 border-b border-border divide-x divide-border">
        <StatCell label="Status">
          {result ? (
            <span className={`font-mono font-semibold ${statusColor(result.status)}`}>
              {result.status}
            </span>
          ) : (
            <span className="text-fg-dim">—</span>
          )}
        </StatCell>
        <StatCell label="Latency">
          {result ? (
            <span className="font-mono text-fg">{result.latency_ms} ms</span>
          ) : (
            <span className="text-fg-dim">—</span>
          )}
        </StatCell>
        <StatCell label="Revision">
          {result?.revision_used?.revision_no != null ? (
            <span className="font-mono text-fg">rev #{result.revision_used.revision_no}</span>
          ) : result?.revision_no != null ? (
            <span className="font-mono text-fg">#{result.revision_no}</span>
          ) : (
            <span className="text-fg-dim">—</span>
          )}
        </StatCell>
        <StatCell label="Result">
          {result ? (
            <span className={isError ? "text-danger" : "text-success"}>
              {isError ? "Error" : "OK"}
            </span>
          ) : (
            <span className="text-fg-dim">—</span>
          )}
        </StatCell>
      </div>

      {/* Tabs: Response | Headers */}
      <Tabs.Root
        value={tab}
        onValueChange={(v) => setTab(v as "response" | "headers")}
        className="flex flex-col"
      >
        <div className="flex items-center border-b border-border px-4">
          <Tabs.List className="flex gap-1 flex-1">
            {(["response", "headers"] as const).map((t) => (
              <Tabs.Trigger
                key={t}
                value={t}
                className="border-b-2 border-transparent px-2 pb-2 pt-2.5 text-xs font-medium capitalize text-fg-muted data-[state=active]:border-accent data-[state=active]:text-fg"
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {result && (
            <div className="flex items-center gap-2 ml-2">
              {tab === "response" && !isError && (
                <button
                  type="button"
                  className="rounded border border-border bg-surface px-2 py-0.5 text-[10px] font-medium text-fg-muted hover:text-fg"
                  onClick={() => setShowRaw((s) => !s)}
                >
                  {showRaw ? "Structured" : "Raw JSON"}
                </button>
              )}
              <button
                type="button"
                aria-label="Copy response"
                className="rounded border border-border bg-surface p-1 text-fg-dim hover:text-fg"
                onClick={handleCopyResponse}
              >
                <Copy size={12} />
              </button>
            </div>
          )}
        </div>

        <Tabs.Content value="response" className="p-4">
          {!result && (
            <div className="text-xs text-fg-muted">Send a request to see the response here.</div>
          )}
          {result && (
            <>
              <WarningsBanner
                warnings={warnings as Parameters<typeof WarningsBanner>[0]["warnings"]}
              />
              {showRaw ? (
                <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-[11px] text-fg leading-relaxed whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
                  {JSON.stringify(body ?? result.error, null, 2)}
                </pre>
              ) : (
                renderStructured()
              )}
            </>
          )}
        </Tabs.Content>

        <Tabs.Content value="headers" className="p-4">
          {!result && (
            <div className="text-xs text-fg-muted">Send a request to see response headers.</div>
          )}
          {result && (
            <pre className="overflow-x-auto rounded-md border border-border bg-surface-2 p-3 font-mono text-[11px] text-fg leading-relaxed whitespace-pre-wrap break-all max-h-[240px] overflow-y-auto">
              {headersText}
            </pre>
          )}
        </Tabs.Content>
      </Tabs.Root>

      {/* History list */}
      <div className="border-t border-border flex-1 flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b border-border">
          <span className="text-[10px] font-medium uppercase tracking-wider text-fg-dim">
            History ({history.length}/{20})
          </span>
        </div>

        <div className="overflow-y-auto max-h-[220px] divide-y divide-border">
          {history.length === 0 && (
            <div className="px-4 py-3 text-xs text-fg-muted">No history yet.</div>
          )}
          {history.map((entry, i) => (
            <button
              key={`${entry.ts}-${i}`}
              type="button"
              className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-2 transition-colors"
              onClick={() => onRestore(entry)}
            >
              <span
                className={`font-mono text-[11px] font-semibold w-8 shrink-0 ${statusColor(entry.status)}`}
              >
                {entry.status}
              </span>
              <span className="text-[11px] text-fg-muted flex-1 truncate">
                {formatTs(entry.ts)}
              </span>
              <span className="text-[11px] font-mono text-fg-dim shrink-0">
                {entry.latency_ms} ms
              </span>
              {entry.revision_no != null && (
                <span className="text-[10px] text-fg-dim shrink-0">#{entry.revision_no}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Footer: Clear button */}
      <div className="border-t border-border px-4 py-2.5 flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          disabled={history.length === 0}
          onClick={() => setClearOpen(true)}
          aria-label="Clear history"
        >
          <Trash2 size={12} />
          Clear history
        </Button>
      </div>

      <ConfirmDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title="Clear history"
        description="This will remove all history entries for this model. This cannot be undone."
        confirmLabel="Clear"
        onConfirm={() => {
          onClear();
          setClearOpen(false);
        }}
      />
    </div>
  );
}

function StatCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-3 py-2 gap-0.5">
      <span className="text-[9px] uppercase tracking-wider text-fg-dim">{label}</span>
      <span className="text-xs">{children}</span>
    </div>
  );
}
